// UI strings for Lausepino (the sentence stack: app/src/routes/SentenceStack.tsx), Finnish
// (default) and English side by side. Finnish is written in full and never truncated; en must
// have exactly the same shape.
import { defineArea } from '../area';

type CheckKey = 'forms' | 'agreement' | 'government' | 'roles';
type TabKey = 'todo' | 'approved' | 'skipped';

export const stackFi = {
  title: 'Lausepino',
  tabs: { todo: 'Läpikäymättä', approved: 'Hyväksytyt', skipped: 'Ohitetut' } as Record<TabKey, string>,
  tabsLabel: 'Näytä',
  onlyRulesOk: 'Vain sääntöjen mukaan oikeat',
  rulesOk: 'Sääntöjen mukaan oikein',
  rulesOkHint:
    'Sovellus ei löytänyt käännöksestä virhettä: jokainen sana on tunnettu taivutusmuoto, sanat taipuvat yhteen ja takaisin luettuna lauseen roolit säilyvät. Merkintä ei korvaa teidän hyväksyntäänne.',
  lookCloser: 'Katsokaa tarkemmin',
  failed: {
    forms: 'jokin sana ei ole tunnettu taivutusmuoto',
    agreement: 'sanat eivät taivu yhteen',
    government: 'jokin sana on väärässä sijassa',
    roles: 'takaisin luettuna jonkin sanan rooli voi muuttua',
  } as Record<CheckKey, string>,
  unknown: (words: string) => `Valyrian sanaa ei löytynyt: ${words}`,
  noHv: 'Sovellus ei osannut kääntää tätä lausetta. Voit kirjoittaa käännöksen itse kohdassa Korjaa.',
  alt: 'Toinen ehdotus',
  useAlt: 'Käytä tätä',
  approve: 'Hyväksy',
  fix: 'Korjaa',
  skip: 'Ohita',
  undo: 'Palauta pinoon',
  /** An approved sentence can be recorded right here (RecordPrompt; also Lue lauseet ääneen). */
  record: 'Äänitä lause',
  recordClose: 'Sulje äänitys',
  saveFixed: 'Hyväksy korjattuna',
  fields: { hv: 'Valyriaksi', fi: 'Suomeksi', en: 'Englanniksi' },
  check: 'Tarkistus',
  checkOk: 'Sovellus ei löytänyt käännöksestä virhettä.',
  checkWaiting: 'Tarkistetaan…',
  checkWord: (what: string, word: string) => `${what} (${word})`,
  hvRequired: 'Kirjoita käännös valyriaksi.',
  approvedBy: (name: string) => `Hyväksynyt: ${name}`,
  skippedBy: (name: string) => `Ohittanut: ${name}`,
  edited: 'korjattu',
  pending: 'odottaa lähetystä',
  more: (n: number) => `Näytä lisää (${n} jäljellä)`,
  empty: {
    todo: 'Kaikki lauseet on käyty läpi.',
    approved: 'Ei vielä hyväksyttyjä lauseita.',
    skipped: 'Ei ohitettuja lauseita.',
  } as Record<TabKey, string>,
  emptyFilter: 'Tällä rajauksella ei ole lauseita.',
  loading: 'Ladataan lauseita…',
  loadError: 'Lauseita ei voitu ladata. Yritä uudelleen, kun yhteys toimii.',
  noFile: 'Lausepinoa ei ole vielä käännetty tähän versioon.',
  count: (n: number) => n.toLocaleString('fi-FI'),
  themes: { daily: 'Arki', plans: 'Suunnitelmat', talk: 'Keskustelu', letters: 'Kirjeet', world: 'Maailma' } as Record<string, string>,
  // ---- layout v2: one sentence at a time (docs/design/SIVUPALSTA.md "Lausepino") ----
  /** The progress line: what is left, and in which order. */
  left: (n: string) => `${n} jäljellä · helpoimmat ensin`,
  /** The last decision, with a way back. */
  lastApproved: 'Hyväksytty',
  lastSkipped: 'Ohitettu',
  undoLast: 'Kumoa',
  /** PC margin notes. */
  words: 'Sanat',
  wordUnknown: 'Tätä muotoa ei vielä tunnisteta.',
  moreReadings: (n: number) => (n === 1 ? '+1 muu luenta' : `+${n} muuta luentaa`),
  source: 'Lähde',
  sourceLine: (theme: string, topic: string) => `${theme} · ${topic}`,
  decide: 'Ratkaisu',
  keys: 'Näppäimet: H hyväksyy, K korjaa, O ohittaa.',
};

export const stackEn: typeof stackFi = {
  title: 'Sentence stack',
  tabs: { todo: 'To go through', approved: 'Approved', skipped: 'Skipped' },
  tabsLabel: 'Show',
  onlyRulesOk: 'Only those correct by the rules',
  rulesOk: 'Correct by the rules',
  rulesOkHint:
    'The app found no error in the translation: every word is a known form, the words agree, and read back the roles of the sentence stay the same. The mark does not replace your approval.',
  lookCloser: 'Take a closer look',
  failed: {
    forms: 'a word is not a known form',
    agreement: 'the words do not agree',
    government: 'a word is in the wrong case',
    roles: 'read back, the role of a word may change',
  },
  unknown: (words) => `No High Valyrian word found for: ${words}`,
  noHv: 'The app could not translate this sentence. You can write the translation yourself under Fix.',
  alt: 'Another suggestion',
  useAlt: 'Use this',
  approve: 'Approve',
  fix: 'Fix',
  skip: 'Skip',
  undo: 'Back to the stack',
  record: 'Record the sentence',
  recordClose: 'Close the recorder',
  saveFixed: 'Approve as fixed',
  fields: { hv: 'High Valyrian', fi: 'Finnish', en: 'English' },
  check: 'Check',
  checkOk: 'The app found no error in the translation.',
  checkWaiting: 'Checking…',
  checkWord: (what, word) => `${what} (${word})`,
  hvRequired: 'Write the High Valyrian translation.',
  approvedBy: (name) => `Approved by ${name}`,
  skippedBy: (name) => `Skipped by ${name}`,
  edited: 'fixed',
  pending: 'waiting to be sent',
  more: (n) => `Show more (${n} left)`,
  empty: {
    todo: 'Every sentence has been gone through.',
    approved: 'No approved sentences yet.',
    skipped: 'No skipped sentences.',
  },
  emptyFilter: 'No sentences with this filter.',
  loading: 'Loading sentences…',
  loadError: 'The sentences could not be loaded. Try again when you are online.',
  noFile: 'The sentence stack has not been translated for this version yet.',
  count: (n) => n.toLocaleString('en-GB'),
  themes: { daily: 'Daily life', plans: 'Plans', talk: 'Conversation', letters: 'Letters', world: 'The world' },
  left: (n) => `${n} left · easiest first`,
  lastApproved: 'Approved',
  lastSkipped: 'Skipped',
  undoLast: 'Undo',
  words: 'Words',
  wordUnknown: 'This form is not recognised yet.',
  moreReadings: (n) => (n === 1 ? '+1 more reading' : `+${n} more readings`),
  source: 'Source',
  sourceLine: (theme, topic) => `${theme} · ${topic}`,
  decide: 'Decision',
  keys: 'Keys: H approves, K fixes, O skips.',
};

// Messages.stack (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('stack', stackFi, stackEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof stackFi> {}
}
